import React, { useState, useCallback } from 'react';
import { TextField, IconButton, InputAdornment } from '@mui/material';
import TodayIcon from '@mui/icons-material/Today';
import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker as MuiDateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { TimePicker as MuiTimePicker } from '@mui/x-date-pickers/TimePicker';
import { usePickerContext } from '@mui/x-date-pickers/hooks';

function mergeSx(base, sx) {
  if (!sx) return base;
  if (typeof sx === 'function') {
    return theme => ({ ...base, ...sx(theme) });
  }
  if (Array.isArray(sx)) {
    return theme =>
      sx.reduce((merged, item) => ({ ...merged, ...(typeof item === 'function' ? item(theme) : item) }), {
        ...base
      });
  }
  return { ...base, ...sx };
}

const PLAIN_FIELD_INPUT_HEIGHT = '30px';

/**
 * Both picker popups — the desktop Popper and the mobile Dialog — portal to
 * `document.body` and land on MUI's modal layer (1300). That is one below any
 * surface which has raised itself ABOVE that layer, and this app has one:
 * ChatGPTPresetDrawer runs at `modal + 1` so it can open from inside a dialog
 * (the meeting note). A picker opened from within that drawer was therefore
 * painted underneath it — invisible on a wide screen, and on a phone visible
 * beside the panel but impossible to tap, because every tap landed on the
 * drawer's paper instead.
 *
 * Raised here rather than at that one call site: a picker popup is transient and
 * belongs on top of whatever opened it, so the next raised surface should not
 * have to rediscover this.
 */
const popupZIndex = theme => theme.zIndex.modal + 2;

/**
 * Caller `slotProps` for the popup slots win, minus the z-index, which is merged
 * into their `sx`. Both slots accept a function of ownerState as well as an
 * object, so both shapes are handled — ClientV2's 生日 field passes an object
 * carrying the body-scroll-lock exemption, and dropping it would kill scrolling
 * of the year list on iOS.
 */
function withPopupZIndex(callerValue) {
  const raise = resolved => ({ ...resolved, sx: mergeSx({ zIndex: popupZIndex }, resolved.sx) });
  if (typeof callerValue === 'function') {
    return ownerState => raise(callerValue(ownerState) || {});
  }
  return raise(callerValue || {});
}

/**
 * MUI X v9 Picker drop-in replacements that:
 *
 *   1. **Open on field-click** (not only via the right-edge calendar icon).
 *      v9 removed `enableAccessibleFieldDOMStructure={false}` (the v8 escape
 *      hatch). Default v9 `PickersTextField` only opens via the icon button.
 *
 *   2. **Render the selected value visibly.** The default v9 `PickersTextField`
 *      uses a split-section `MuiPickersInputBase-sectionsContainer` whose
 *      glyphs intermittently fail to paint in this codebase (text exists in
 *      DOM but field appears empty). Replacing the `field` slot with a plain
 *      `TextField` showing the formatted value sidesteps that entirely.
 *
 * Drop-in replacements for `@mui/x-date-pickers/{DatePicker,DateTimePicker,TimePicker}`.
 */

// Plain-input field slot — shows the formatted value as a readonly TextField,
// opens the picker on click. Uses the picker context for value + open state.
function PlainFieldSlot(props) {
  const ctx = usePickerContext();
  const { setOpen, value, fieldFormat, label, disabled, readOnly, triggerRef } = ctx;
  const { InputProps = {}, inputProps = {}, className, id, name, sx } = props;
  // Format value if it's moment-like / dayjs-like
  let formatted = '';
  if (value && typeof value.format === 'function') {
    formatted = value.format(fieldFormat || 'DD-MMM-YYYY');
  } else if (value && typeof value === 'string') {
    formatted = value;
  }
  const handleClick = e => {
    if (disabled || readOnly) return;
    if (e.target.closest('button')) return;
    setOpen(true);
  };
  // v4-parity calendar-icon affordance on the field. The Clear button itself
  // lives in the picker's action bar (configured in makeClickable), matching the
  // v4 mobile dialog's "Clear / Cancel / OK" layout — a field "×" adornment is
  // not how v4 exposed clearing.
  const endAdornment = (
    <InputAdornment position="end">
      <IconButton
        aria-label="Open date picker"
        size="small"
        edge="end"
        disabled={disabled || readOnly}
        onClick={e => {
          e.stopPropagation();
          setOpen(true);
        }}
      >
        <TodayIcon color="action" fontSize="small" />
      </IconButton>
      {InputProps.endAdornment}
    </InputAdornment>
  );
  return (
    <TextField
      ref={triggerRef}
      className={className}
      id={id}
      label={label}
      name={name}
      value={formatted}
      disabled={disabled}
      onClick={handleClick}
      slotProps={{
        input: {
          ...InputProps,
          readOnly: true,
          sx: mergeSx(
            {
              height: PLAIN_FIELD_INPUT_HEIGHT,
              alignItems: 'center',
              cursor: disabled || readOnly ? 'default' : 'pointer',
              '& .MuiInputBase-input': {
                height: PLAIN_FIELD_INPUT_HEIGHT,
                boxSizing: 'border-box',
                lineHeight: PLAIN_FIELD_INPUT_HEIGHT,
                padding: '0 6px'
              }
            },
            InputProps.sx
          ),
          endAdornment
        },
        htmlInput: { ...inputProps, 'aria-readonly': true }
      }}
      sx={mergeSx({ width: '100%' }, sx)}
    />
  );
}

function makeClickable(MuiComponent, displayName, componentDefaults = {}) {
  const Wrapped = props => {
    const { open: openProp, onOpen, onClose, slots, slotProps, className, sx, ...rest } = props;
    const [internalOpen, setInternalOpen] = useState(false);
    const isControlled = openProp !== undefined;
    const open = isControlled ? openProp : internalOpen;
    const callerSlotProps = slotProps || {};
    const fieldSlotProps = callerSlotProps.field || {};
    const hasFieldSx = !!sx || !!fieldSlotProps.sx;
    const fieldSx = hasFieldSx ? mergeSx(sx || {}, fieldSlotProps.sx) : undefined;

    const handleOpen = useCallback(() => {
      if (!isControlled) setInternalOpen(true);
      onOpen?.();
    }, [isControlled, onOpen]);

    const handleClose = useCallback(() => {
      if (!isControlled) setInternalOpen(false);
      onClose?.();
    }, [isControlled, onClose]);

    return (
      <MuiComponent
        {...componentDefaults}
        {...rest}
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        slots={{ field: PlainFieldSlot, ...(slots || {}) }}
        slotProps={{
          ...callerSlotProps,
          // v4-parity action bar: "Clear / Cancel / OK" on mobile, "Clear" on
          // desktop (where day-click still auto-accepts). Callers can override
          // by passing their own slotProps.actionBar.
          actionBar:
            callerSlotProps.actionBar ||
            (({ variant }) => ({
              actions: variant === 'desktop' ? ['clear'] : ['clear', 'cancel', 'accept'],
              // v4 parity: action buttons in primary colour (this app's theme
              // defaults MuiButton to color="inherit", which renders them grey),
              // and Clear pushed to the left with Cancel / OK on the right.
              sx: {
                '& .MuiButton-root': { color: 'primary.main' },
                '& .MuiButton-root:first-of-type': { marginRight: 'auto' }
              }
            })),
          popper: withPopupZIndex(callerSlotProps.popper),
          dialog: withPopupZIndex(callerSlotProps.dialog),
          field: {
            ...fieldSlotProps,
            className: [className, fieldSlotProps.className].filter(Boolean).join(' ') || undefined,
            sx: fieldSx
          }
        }}
      />
    );
  };
  Wrapped.displayName = displayName;
  return Wrapped;
}

// `openTo` / `views` are intentionally NOT defaulted here — each call site keeps
// its own (e.g. AddPolicy uses openTo="year", MarketValueUpdateDate uses
// openTo="day"), matching the per-field v4 behaviour. All three pickers still
// get the action-bar Clear / Cancel / OK via makeClickable.
const ClickableDatePicker = makeClickable(MuiDatePicker, 'ClickableDatePicker');
const ClickableDateTimePicker = makeClickable(MuiDateTimePicker, 'ClickableDateTimePicker');
const ClickableTimePicker = makeClickable(MuiTimePicker, 'ClickableTimePicker');

export { ClickableDatePicker as DatePicker };
export { ClickableDateTimePicker as DateTimePicker };
export { ClickableTimePicker as TimePicker };
export default ClickableDatePicker;
